/**
 * Settings Page
 * Tabs are gated by the tenant's plan features — a tab whose feature the plan
 * doesn't include is hidden, so tenants only see what their subscription grants.
 * (Backend also enforces each feature via requireFeature; this keeps the UI honest.)
 */
import PageHeader from '@/components/ui/PageHeader';
import { Box, Tab, Tabs } from '@mui/material';
import React from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import AutomationSettings from './AutomationSettings';
import EscalationSettings from './EscalationSettings';
import ExternalGatewayConfig from './ExternalGatewayConfig';
import CustomFieldsManager from './CustomFieldsManager';
import LeadScoringSettings from './LeadScoringSettings';
import InstagramConfig from './InstagramConfig';
import LLMConfig from './LLMConfig';
import RagVectorConfig from './RagVectorConfig';
import PrivacyPolicySettings from './PrivacyPolicySettings';
import { usePlanFeature } from '@/hooks/usePlanFeature';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { TenantFeatures } from '@/store/slices/authSlice';

interface TabDef {
  key: string;
  label: string;
  /** Plan feature gating this tab; undefined = no feature gate. */
  feature?: keyof TenantFeatures;
  /** Module gating this tab (tenant.modules key); undefined = no module gate. */
  module?: string;
  render: () => React.ReactNode;
}

const TAB_DEFS: TabDef[] = [
  { key: 'instagram', label: 'Instagram', module: 'instagram', render: () => <InstagramConfig /> },
  { key: 'automation', label: 'Automation', feature: 'workflowsEnabled', module: 'automation', render: () => <AutomationSettings /> },
  // EscalationSettings calls GET/PATCH /admin/routing-settings, which shares
  // the 'automation' module key (see admin.routes.ts's migration).
  { key: 'handoff', label: 'Human Handoff', module: 'automation', render: () => <EscalationSettings /> },
  { key: 'llm', label: 'LLM Configuration', module: 'llm', render: () => <LLMConfig /> },
  { key: 'rag', label: 'RAG Vector DB', feature: 'enableRag', render: () => <RagVectorConfig /> },
  { key: 'integrations', label: 'API Integration', feature: 'apiAccess', module: 'integrations', render: () => <ExternalGatewayConfig /> },
  { key: 'fields', label: 'Custom Fields', module: 'verticals', render: () => <CustomFieldsManager scope="crm" /> },
  // Flow variables live under AUTOMATION, not verticals — a tenant builds flows
  // without the field-schema module. Same gate as the Automation tab above.
  { key: 'flow-vars', label: 'Variables', feature: 'workflowsEnabled', module: 'automation', render: () => <CustomFieldsManager scope="flow" /> },
  { key: 'lead-scoring', label: 'Lead Scoring', module: 'leadScoring', render: () => <LeadScoringSettings /> },
  { key: 'privacy-policy', label: 'Privacy Policy', module: 'privacyPolicy', render: () => <PrivacyPolicySettings /> },
];

const Settings: React.FC = () => {
  const { hasFeature } = usePlanFeature();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasModuleAccess } = useModuleAccess();

  // A tab shows only if its feature (if any) AND its module (if any) are granted.
  const visibleTabs = TAB_DEFS.filter(
    (t) => (!t.feature || hasFeature(t.feature)) && (!t.module || hasModuleAccess(t.module)),
  );

  const [tab, setTab] = React.useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    const idx = requested ? visibleTabs.findIndex((t) => t.key === requested) : -1;
    return idx >= 0 ? idx : 0;
  });

  const safeTab = Math.min(tab, Math.max(0, visibleTabs.length - 1));
  const active = visibleTabs[safeTab];

  // This page already READ `?tab=<key>` on mount but never wrote it back, so the
  // active tab was unshareable and lost on return. Mirroring it out closes that
  // loop. It stays keyed by `t.key`, never by index: `visibleTabs` is filtered by
  // plan and permissions, so index 2 is a different tab for different people and
  // an index-based link would open the wrong one.
  React.useEffect(() => {
    // Skipped while the legacy `?tab=whatsapp` link is being forwarded below —
    // writing here would race that redirect.
    if (!active || searchParams.get('tab') === 'whatsapp') return;
    if (searchParams.get('tab') === active.key) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', active.key);
    // Through the router, not history.replaceState: the latter left the router's
    // own location stale, so anything reading useLocation() saw the old query.
    // `replace` keeps tab switches out of the history stack.
    setSearchParams(next, { replace: true });
  }, [active, searchParams, setSearchParams]);

  // The WhatsApp tab moved to its own page (sidebar › WhatsApp Templates);
  // forward old ?tab=whatsapp links there.
  if (new URLSearchParams(location.search).get('tab') === 'whatsapp') {
    const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
    return <Navigate to={`${prefix}/whatsapp-templates`} replace />;
  }

  return (
    <Box>
      <PageHeader title="Settings" subtitle="Configure your chatbot platform" />

      <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 3 }}>
        <Tabs value={safeTab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {visibleTabs.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      </Box>

      {/* WhatsApp connection (Meta app/phone/access token) is managed by the
          platform team via the super-admin console, not self-service — only
          Message Templates stays tenant-editable. Instagram Login is a real
          OAuth consent screen the account owner must personally complete, so it
          stays tenant self-service. */}
      {active?.render()}
    </Box>
  );
};

export default Settings;
